import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import SupplierContext from "../../../../contexts/SupplierContext";
import UserContext from "../../../../contexts/UserContext";
import { OwAccordion } from "../../../shared/OwAccordion/OwAccordion";
import wayfairLogo from "../../../../assets/images/wayfair/wayfair-logo.svg";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import IUpdateSupplierIntegrationsDTO from "../../../../models/DTO/IUpdateSupplierIntegrationsDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IOnUpdateSupplierIntegrationsDTO from "../../../../models/DTO/IOnUpdateSupplierIntegrationsDTO";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwButton from "../../../shared/OwButton/OwButton";
import IWayfairTestConnectionDTO from "../../../../models/DTO/IWayfairTestConnectionDTO";
import IWayfairOnTestConnectionDTO from "../../../../models/DTO/IWayfairOnTestConnectionDTO";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import IOnGetCatalogsDTO from "../../../../models/DTO/IOnGetCatalogsDTO";
import IGetCatalogsDTO from "../../../../models/DTO/IGetCatalogsDTO";
import ICatalog from "../../../../models/ICatalog";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import IPrice_Group from "../../../../models/IPrice_Group";
import { useNavigate } from "react-router-dom";
import * as _ from "lodash-es";
import { Socket } from "socket.io-client";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

export const WayfairConnect: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const navigate = useNavigate();

    const [isDisconnectActive, setIsDisconnectActive] = useState<boolean>(false);
    const [wayfairDetails, setWayfairDetails] = useState<{
        clientId: string;
        clientSecret: string;
        supplierId: string;
        autoAcceptOrders: boolean;
        defaultInventoryLevel: number;
    }>({
        clientId: supplierModel.Supplier_Integration.wayfair_client_id || "",
        clientSecret: "",
        supplierId: supplierModel.Supplier_Integration.wayfair_supplier_id || "",
        autoAcceptOrders: supplierModel.Supplier_Integration.wayfair_auto_accept_orders || false,
        defaultInventoryLevel: supplierModel.Supplier_Integration.wayfair_default_inventory_value || 0,
    });
    const [saveActive, setSaveActive] = useState<boolean>(false);
    const [testActive, setTestActive] = useState<boolean>(false);
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [priceGroups, setPriceGroups] = useState<IPrice_Group[]>([]);
    const [wayfairCatalogId, setWayfairCatalogId] = useState<number | undefined | null>(
        supplierModel.Supplier_Integration.wayfair_catalog_id
    );
    const [wayfairPriceGroupId, setWayfairPriceGroupId] = useState<number | undefined | null>(
        supplierModel.Supplier_Integration.wayfair_price_group_id
    );

    const has_wayfair = supplierModel.Supplier_Integration.has_wayfair;

    useEffect(() => {
        props.socket.on(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
        props.socket.on(EndpointsEnum.WayfairOnTestConnection, onTestConnection);
        props.socket.on(EndpointsEnum.Notify, notify);
        props.socket.on(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);

        getCatalogs();

        return () => {
            props.socket.removeListener(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onUpdateSupplierIntegrations);
            props.socket.removeListener(EndpointsEnum.WayfairOnTestConnection, onTestConnection);
            props.socket.removeListener(EndpointsEnum.Notify, notify);
            props.socket.removeListener(EndpointsEnum.CatalogsOnGetCatalogs, onGetCatalogs);
        };
    }, []);

    useEffect(() => {
        handleCatalogSelection(wayfairCatalogId);
    }, [catalogs]);

    const notify = () => {
        setTestActive(false);
        setSaveActive(false);
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            callMeta: {
                view: "WayfairConnect",
                fn: "getCatalogs",
            },
        };

        props.socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);
    };

    const viewLogs = () => {
        navigate("/wayfair/logs");
    };

    const getConnectedStatus = () => {
        return (
            <div className={"ms-auto d-flex align-items-center justify-content-center"}>
                {has_wayfair ? <OwButton fn={viewLogs} title={"view_logs"} className={"me-3 btn-primary"} /> : null}
                <OwToggle fn={toggleWayfair} name={"has_wayfair"} label={""} active={!!has_wayfair} />
            </div>
        );
    };

    const toggleWayfair = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                has_wayfair: !has_wayfair,
            },
        };

        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const onUpdateSupplierIntegrations = (data: IOnUpdateSupplierIntegrationsDTO) => {
        setSaveActive(false);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        const existingWayfairDetails = {
            ...wayfairDetails,
            [name]: value,
        };

        setWayfairDetails(existingWayfairDetails);
    };

    const saveClientDetails = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                wayfair_client_id: wayfairDetails.clientId,
            },
        };

        if (wayfairDetails.clientSecret.length) {
            payload.Supplier_Integration.wayfair_client_secret = wayfairDetails.clientSecret;
        }

        if (wayfairCatalogId) {
            payload.Supplier_Integration.wayfair_catalog_id = wayfairCatalogId;
        }

        if (wayfairPriceGroupId) {
            payload.Supplier_Integration.wayfair_price_group_id = wayfairPriceGroupId;
        }

        if (wayfairDetails.supplierId.length) {
            payload.Supplier_Integration.wayfair_supplier_id = wayfairDetails.supplierId;
        }

        if (!_.isNil(wayfairDetails.autoAcceptOrders)) {
            payload.Supplier_Integration.wayfair_auto_accept_orders = wayfairDetails.autoAcceptOrders;
        }

        if (wayfairDetails.defaultInventoryLevel >= 0) {
            payload.Supplier_Integration.wayfair_default_inventory_value = wayfairDetails.defaultInventoryLevel;
        }

        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);

        setSaveActive(true);
    };

    const testConnection = () => {
        const payload: IWayfairTestConnectionDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.WayfairTestConnection, payload);

        setTestActive(true);
    };

    const onTestConnection = (data: IWayfairOnTestConnectionDTO) => {
        const notification: INotification = {
            message: "successfully_tested_wayfair_connection",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        props.notifyFn(null, notification);

        setTestActive(false);
    };

    const selectCatalog = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        handleCatalogSelection(value);
    };

    const handleCatalogSelection = (value: any) => {
        if (value) {
            for (const catalog of catalogs) {
                if (catalog.id.toString() === value.toString()) {
                    setWayfairCatalogId(catalog.id);
                    setPriceGroups(catalog.Price_Groups || []);
                    handlePriceGroupSelection(wayfairPriceGroupId);
                }
            }
        }
    };

    const selectPriceGroup = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        handlePriceGroupSelection(value);
    };

    const handlePriceGroupSelection = (value: any) => {
        if (value) {
            for (const priceGroup of priceGroups) {
                if (priceGroup.id.toString() === value.toString()) {
                    setWayfairPriceGroupId(priceGroup.id);
                }
            }
        }
    };

    const getMainContent = () => {
        return (
            <div>
                <div>
                    <OwLabel title={"supplier_id"} />
                    <OwInputWrapper name={"supplierId"} fn={handleChange} value={wayfairDetails.supplierId} />
                </div>

                <div>
                    <OwLabel title={"client_id"} />
                    <OwInputWrapper name={"clientId"} fn={handleChange} value={wayfairDetails.clientId} />
                </div>

                <div>
                    <OwLabel title={"client_secret"} />
                    <OwInputWrapper name={"clientSecret"} fn={handleChange} value={wayfairDetails.clientSecret} />
                    <div>
                        <OwLabel title={"secret_set"} className={"me-3"} />
                        {supplierModel.Supplier_Integration.wayfair_client_secret ? (
                            <span className={"text-success"}>Y</span>
                        ) : (
                            <span className={"text-danger"}>N</span>
                        )}
                    </div>
                </div>

                <div>
                    <OwLabel title={"catalog"} />
                    <OwSelectOption
                        options={catalogs}
                        titleProp={"name"}
                        valueProp={"id"}
                        name={"catalog"}
                        fn={selectCatalog}
                        selectedOption={wayfairCatalogId}
                        hasSelectedOptionProp={false}
                    />
                </div>

                <div>
                    <OwLabel title={"price_group"} />
                    <OwSelectOption
                        options={priceGroups}
                        titleProp={"value"}
                        valueProp={"id"}
                        name={"price_group"}
                        fn={selectPriceGroup}
                        selectedOption={wayfairPriceGroupId}
                        hasSelectedOptionProp={false}
                    />
                </div>

                <div className={"mt-2"}>
                    <OwToggle
                        labelOnRight={true}
                        fn={handleChange}
                        name={"autoAcceptOrders"}
                        label={"auto_accept_orders"}
                        active={wayfairDetails.autoAcceptOrders}
                    />
                </div>

                <div className={"mt-2"}>
                    <div>
                        <OwLabel title={"default_inventory_level"} />
                        <OwInputWrapper name={"defaultInventoryLevel"} fn={handleChange} value={wayfairDetails.defaultInventoryLevel} />
                    </div>
                </div>

                <div className={"mt-3"}>
                    <OwButton title={"save"} fn={saveClientDetails} active={saveActive} disableOnActive={true} />

                    <OwButton
                        className={"btn-secondary ms-3"}
                        title={"test"}
                        fn={testConnection}
                        active={testActive}
                        disableOnActive={true}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className={"WayfairConnect"}>
            <OwAccordion
                hideChevron={!has_wayfair}
                leftImage={wayfairLogo}
                leftImageHeight={50}
                leftImageAlt={"Wayfair"}
                rightElem={getConnectedStatus()}>
                {getMainContent()}
            </OwAccordion>
        </div>
    );
};
